import { useEffect, useState, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { addMessageListener, IframeComponent, sendMessage } from '@hcecbp/uiintegrationcomponent';
import authService, { getMCDEnabled } from "../../../authService";
import { IOrganisationState } from "../../commonUtils/Redux/Global/globalShared";
import './index.scss';
import { Loader } from "@scuf/common";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getFeatureFlagValuebasedOnSIOrg, getFeatureFlagValuebasedOnSIOrgWhitelist, isFeatureFlagEnabledForUser } from '../../commonUtils/LDFlagUtils';
import { useHistory } from "react-router-dom";
import i18next from "i18next";
import { ContentLoader } from "@forge/common";

const LandingPageDashboard = () => {
    const orgSelected = useSelector((state: IOrganisationState) => state.globalShared.gatewaySelectedOrganisation);
    const userList = useSelector((state: IOrganisationState) => state.globalShared.userList);
    const organisations = useSelector(
    (state: IOrganisationState) => state.globalShared.organisationList
    );
    const environmentVariables = (window as any).ENV;
    const USER_LOGGEDIN = "oidcUserLoggedIn";
    const USER_LIST = "userList";
    const LANDING_PAGE_DASHBOARD_HOST_URL = environmentVariables.LANDING_PAGE_DASHBOARD_HOST_URL;
    const hstName = window.location.href
    const getTheme = () => localStorage.getItem('COLOR_THEME') || '0';
    const getLocale = () => i18next.language || 'en-US';
    const getIframeElement = () => document.getElementById('landing-page-dashboard-iframe-id') as HTMLIFrameElement;
    const getWindowInnerHeight = () => window.innerHeight;
    const getContentHeight = (inputInnerHeight?: number) => (inputInnerHeight || getWindowInnerHeight()) - 50;
    const [isIFrameLoaded, setIsIFrameLoaded] = useState<boolean>(false);
    const [iframeHeight, setIframeHeight] = useState<number>(getContentHeight());
    const SELECTED_LOGIN_OPTION = 'selectedLoginOption';
    const [loading, setLoading] = useState(true);
    const featureFlags = useFlags();

    const history = useHistory();
   
    const getAuthorityClientID = () => {
        const isMCDSSO = window.location.pathname.includes('mcd') || getMCDEnabled(window.location.search) || environmentVariables.MCD_INSTANCE || ((window as any).ENV.ADMIN_MCD_HOSTNAME !== '' ? hstName.includes((window as any).ENV.ADMIN_MCD_HOSTNAME) : false);
        if (isMCDSSO) {
            return `${environmentVariables.AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID_MCD}`
        }
        if (localStorage.getItem(SELECTED_LOGIN_OPTION) === 'SSO') {
            return `${environmentVariables.AUTHORITY_HON_SSO}:${environmentVariables.AUTHORITY_CLIENT_ID_HON_SSO}`;
        }
        return `${environmentVariables.AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID}`;
    };

    const getAccessToken = () => JSON.parse(sessionStorage.getItem(`oidc.user:${getAuthorityClientID()}`) || '{}')?.access_token || '';
    const getProfileData = () => JSON.parse(sessionStorage.getItem(`oidc.user:${getAuthorityClientID()}`) || '{}')?.profile || '';
    
    const featureFlagsProps = {
      ...featureFlags,
      isDistributorFlowEnabled: getFeatureFlagValuebasedOnSIOrg(
        'enableBACSFlowDistributor',
        featureFlags,
        orgSelected,
        organisations
      ),
      enableARRLicenseManagement: getFeatureFlagValuebasedOnSIOrgWhitelist(
        'enableArrLicenseFlowForSi',
        featureFlags,
        orgSelected,
        organisations
      ),
      enableARRLicenseOverviewAndDownloadForSI: authService.isLicensePrivilegedUser,
      isBmsAssistProjectOsSubscriptionEnabled: isFeatureFlagEnabledForUser(
        'bmsAssistProjectOsSubscription',
        featureFlags,
        userList?.email
      ),
      isBmsAssistProjectOsSubscriptionEnabledByOrgId: isFeatureFlagEnabledForUser(
        'bmsAssistProjectOsSubscription',
        featureFlags,
        orgSelected?.id
      ),
    };

    useLayoutEffect(() => {
        const handleResize = () => setIframeHeight(getContentHeight(getWindowInnerHeight()));
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (orgSelected?.id && orgSelected?.name) {
            setLoading(true);
            const timer = setTimeout(() => {
                setLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [orgSelected]);

    useEffect(() => {
        const event = addMessageListener(LANDING_PAGE_DASHBOARD_HOST_URL, (message) => {
            if (message?.type === 'appContext') {
                const receivedVal = JSON.parse(message?.payload?.viewType || '{}');
                const viewPath = message?.payload?.viewPath || '';
                if (receivedVal?.iotLoaded) {
                    setIsIFrameLoaded(true);
                }
                switch (viewPath) {
                    case 'orgDashboard':
                        history.push('/app/feature/organisations');
                        break;
                    case 'gatewayManagement':
                        history.push('/app/feature/gateway-management');
                        break;
                    case 'subscriptionDashboard':
                        history.push(`/app/feature/subscription?subs-status=${receivedVal?.subscriptionStatus || ''}`);
                        break;
                    case 'licenseManagement':
                        history.push('/app/feature/licenses');
                        break;
                    case 'orgDashboardLicenseTab': {
                        const customerId = receivedVal?.customerId || '';
                        const customerName = receivedVal?.customerName || '';
                        history.push(`/app/feature/organisations?customerId=${customerId}&orgName=${customerName}`);
                        break;
                    }
                    case 'licenseManagementFilter':
                        history.push(`/app/feature/licenses?license-filter=${receivedVal?.licenseFilter || ''}`);
                        break;
                    case 'projectManagement':
                        history.push('/app/feature/project-management');
                        break;
                    case 'userManagement':
                        history.push('/app/feature/users');
                        break;
                    default:
                        break;
                }
            }
        });

        return () => {
            if (event) {
                event.dispose();
            }
        };
    }, []);

    const sendViewTypeToIframe = () => {
        const iframeContentWindow = getIframeElement()?.contentWindow;
        if (iframeContentWindow && orgSelected?.id) {
            const viewType = {
                theme: getTheme(),
                locale: getLocale(),
                rbmAccessToken: getAccessToken(),
                profileData: getProfileData(),
                selectedOrg: orgSelected?.id || '',
                selectedOrgName: orgSelected?.name,
                featureFlags: featureFlagsProps,
                globalPermissions: authService.globalPermissions,
                selectedSIOrg: orgSelected || '',
            };
            updateStorage();
            sendMessage(LANDING_PAGE_DASHBOARD_HOST_URL, {
                type: 'appContext',
                payload: {
                    viewType: JSON.stringify(viewType)
                }
            }, iframeContentWindow);
        }
    };

    useEffect(() => {
        if (orgSelected) {
            sendViewTypeToIframe();
        }
    }, [
        orgSelected,
        isIFrameLoaded,
        i18next.language,
        localStorage.getItem('COLOR_THEME')
    ]);

    const updateStorage = () => {
        sessionStorage.setItem(USER_LOGGEDIN, getProfileData()?.given_name || "");
        sessionStorage.setItem(USER_LIST, JSON.stringify(userList));
    }

    return (
        <div>
            {/* IFrame Component */}
            {!isIFrameLoaded && <div className="gateway-loader-cls"><ContentLoader /></div>}
            <div style={{ display: !isIFrameLoaded ? "none" : "block" }}>
                <IframeComponent
                    width="100%"
                    height={`${iframeHeight}px`}
                    id="landing-page-dashboard-iframe-id"
                    url={`${LANDING_PAGE_DASHBOARD_HOST_URL}/landing-page`}
                />
            </div>
        </div>
    );
};

export default LandingPageDashboard;